import { Form, Link, useLocation } from '@remix-run/react'
import { useOptionalUser } from '~/utils/user.ts'
import { rrFlowStyles } from '~/styles/rr-flow.ts'
import { RR_FONT_HREF } from '~/styles/rr-tokens.ts'
import { RR_NAV } from '~/utils/rr-nav.ts'
import { RR_TOTAL_PARTS } from '~/utils/rr-steps.ts'

export const rrMeta = (title: string) => () =>
	[
		{ title: `${title} | Reverse Recruiter` },
		{ name: 'robots', content: 'noindex' },
	]

export { GeneralErrorBoundary as ErrorBoundary } from '~/components/error-boundary.tsx'

export const rrFlowLinks = () => [
	{ rel: 'preconnect', href: 'https://fonts.googleapis.com' },
	{
		rel: 'preconnect',
		href: 'https://fonts.gstatic.com',
		crossOrigin: 'anonymous' as const,
	},
	{ rel: 'stylesheet', href: RR_FONT_HREF },
]

function RRFlowStyles() {
	return <style dangerouslySetInnerHTML={{ __html: rrFlowStyles }} />
}

export function RRFlowShell({
	part,
	children,
}: {
	part: number | null
	children: React.ReactNode
}) {
	return (
		<div className="rrf">
			<RRFlowStyles />
			<div className="rrf-top">
				<Link to="/reverse-recruiter" className="rrf-mark" reloadDocument>
					Reverse Recruiter
				</Link>
				<div className="rrf-top-right">
					{part ? <StepTicks part={part} /> : null}
					<RRAccountMenu />
				</div>
			</div>
			{children}
		</div>
	)
}

function StepTicks({ part }: { part: number }) {
	return (
		<div className="rrf-steps" aria-label={`Part ${part} of ${RR_TOTAL_PARTS}`}>
			{Array.from({ length: RR_TOTAL_PARTS }, (_, i) => (
				<span
					key={i}
					className="rrf-tick"
					data-on={i + 1 <= part ? 'true' : undefined}
					data-now={i + 1 === part ? 'true' : undefined}
				/>
			))}
			<span className="rrf-stepnum">
				{String(part).padStart(2, '0')} /{' '}
				{String(RR_TOTAL_PARTS).padStart(2, '0')}
			</span>
		</div>
	)
}

export function Eyebrow({ children }: { children: React.ReactNode }) {
	return <p className="rrf-eyebrow">{children}</p>
}

export function partLabel(part: number) {
	return `PART ${part} OF ${RR_TOTAL_PARTS}`
}

function RRAccountMenu({ isMember = false }: { isMember?: boolean }) {
	const user = useOptionalUser()
	const location = useLocation()
	if (!user) {
		const back = encodeURIComponent(location.pathname)
		return (
			<Link to={`/login?redirectTo=${back}`} className="rrf-acct-in">
				Sign in
			</Link>
		)
	}
	return (
		<details className="rrf-acct">
			<summary className="rrf-acct-btn">
				<span className="rrf-acct-name">{user.name ?? user.username}</span>
				<span className="rrf-acct-caret" aria-hidden="true" />
			</summary>
			<div className="rrf-acct-menu">
				<Link to={`/users/${user.username}`} className="rrf-acct-item">
					Your profile
				</Link>
				<Link to="/settings/profile" className="rrf-acct-item">
					Account settings
				</Link>
				{isMember ? (
					<Form action="/resources/rr-billing" method="POST">
						<button type="submit" className="rrf-acct-item">
							Manage billing
						</button>
					</Form>
				) : null}
				<Form action="/logout" method="POST">
					<button type="submit" className="rrf-acct-item rrf-acct-item--out">
						Log out
					</button>
				</Form>
			</div>
		</details>
	)
}

export function RRMemberShell({
	current,
	children,
}: {
	current: string
	children: React.ReactNode
}) {
	return (
		<div className="rrf">
			<RRFlowStyles />
			<div className="rrf-top">
				<Link to="/reverse-recruiter/worklog" className="rrf-mark">
					Reverse Recruiter
				</Link>
				<RRAccountMenu isMember />
			</div>
			<div className="rrf-narrow">
				<nav className="rrf-tabs">
					{RR_NAV.map(item => (
						<Link
							key={item.to}
							to={item.to}
							className="rrf-tab"
							data-on={item.to === current ? 'true' : undefined}
						>
							{item.label}
						</Link>
					))}
				</nav>
				{children}
			</div>
		</div>
	)
}
